"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.calculatePayPalFee = void 0;
const paypal_public_1 = require("@joindeed/paypal-public");
const utils_1 = require("../../utils");
const enums_1 = require("../../types/enums");
const calculateTotalFeeAmount_1 = require("./calculateTotalFeeAmount");
const calculateNominalProviderFeeAmount_1 = require("./calculateNominalProviderFeeAmount");
const calculateActualPayPalFeeStructure_1 = require("./calculateActualPayPalFeeStructure");
const calculateNominalApplicationFeeAmount_1 = require("./calculateNominalApplicationFeeAmount");
const DEFAULT_APPLICATION_FEE_CAP = Number.POSITIVE_INFINITY;
const COUNTRY_CODE_LENGTH = 2;
const CAF_APPLICATION_FEE = 3.01;
/**
 * Compute the fees for a PayPal donation:
 *
 * @param {Object} p - The parameters object
 * @param {number} p.amount The original transaction amount selected by the user.
 * @param {string} p.nonprofitCountryCode
 * @param {number} p.applicationFee target application fee for a non-recurring donation without covering fee
 * @param {number} p.applicationFeeAmountCap maximal application fees (eg $250)
 * @param {boolean} p.coverFees true if the user wants to cover the fees (from PayPal and from us)
 * @param {boolean} p.isRecurring true if the donation will be made monthly (then the Vaulting fees apply)
 * @param {PayPalPoweredDonationProvider} p.donationProvider this function only handles PayPal or CAF donations
 * @param {boolean} p.isSandbox true if the payment should be made to the Sandbox environment: fake payment
 * @returns {{totalFeeAmount: number, applicationFeeAmount: number, providerFeeAmount: number}}
 *    values of the fees in the currency of @nonprofitCountryCode
 *    providerFeeAmount: fee from PayPal
 *    applicationFeeAmount: fee from Deed
 *    totalFeeAmount = applicationFeeAmount + providerFeeAmount
 */
const calculatePayPalFee = ({ amount: transactionAmount, nonprofitCountryCode, applicationFee: nominalApplicationFee, applicationFeeAmountCap = DEFAULT_APPLICATION_FEE_CAP, coverFees, isRecurring = false, donationProvider, isSandbox, }) => {
    const isCAFDonation = donationProvider === enums_1.DonationProvider.CAF;
    if (!nonprofitCountryCode || typeof transactionAmount !== 'number') {
        throw new Error(`Invalid arguments for calculating ${donationProvider} Fees`);
    }
    if (nonprofitCountryCode.length !== COUNTRY_CODE_LENGTH) {
        throw new Error(`Invalid country code argument ${nonprofitCountryCode} for calculating ${donationProvider} Fees`);
    }
    // Get PayPal fees depending on country
    const defaultFeeStructure = (0, paypal_public_1.modelFeeStructureForCountry)({ countryCode: nonprofitCountryCode, isSandbox });
    // Get PayPal fees depending on recurrence
    const actualFeeStructure = (0, calculateActualPayPalFeeStructure_1.calculateActualPayPalFeeStructure)({ isRecurring, defaultFeeStructure });
    // Remove the CAP for Application Fee Amount if it's a CAF donation
    const maxApplicationFeeAmount = isCAFDonation ? Number.POSITIVE_INFINITY : applicationFeeAmountCap;
    // Set the
    const applicationFee = isCAFDonation ? CAF_APPLICATION_FEE : nominalApplicationFee;
    // Calculate the nominal provider fee amount
    const nominalProviderFeeAmount = (0, calculateNominalProviderFeeAmount_1.calculateNominalProviderFeeAmount)({
        transactionAmount,
        defaultFeeStructure,
        actualFeeStructure,
    });
    // Calculate the nominal application fee amount, capped at the maximum allowed
    const nominalApplicationFeeAmount = (0, calculateNominalApplicationFeeAmount_1.calculateNominalApplicationFeeAmount)({
        transactionAmount,
        applicationFee,
        maxApplicationFeeAmount,
    });
    // Calculate the total fee amount for a transaction
    const totalFeeAmount = (0, calculateTotalFeeAmount_1.calculateTotalFeeAmount)({
        transactionAmount,
        actualFeeStructure,
        nominalProviderFeeAmount,
        nominalApplicationFeeAmount,
        coverFees,
    });
    // Calculate the total amount based for a transaction based on the total fee amount
    const totalAmount = transactionAmount + (coverFees ? totalFeeAmount : 0);
    // Calculate the provider fee amount based on totalAmount
    const providerFeeAmount = (0, utils_1.roundNumber)(totalAmount * (actualFeeStructure.relativeFee / 100) + actualFeeStructure.fixedFee);
    // Calculate the application fee amount, which is the difference between the total and provider fee amounts
    const applicationFeeAmount = Math.max((0, utils_1.roundNumber)(totalFeeAmount - providerFeeAmount), 0);
    return {
        totalFeeAmount,
        applicationFeeAmount,
        providerFeeAmount,
    };
};
exports.calculatePayPalFee = calculatePayPalFee;
//# sourceMappingURL=calculatePayPalFee.js.map